import { useState } from "react";
import S from "../styles/styles";

// -- Helpers ----------------------------------------------------------------
const today = () => new Date().toISOString().split("T")[0];
const fmtC  = (n) => n ? `$${parseFloat(n).toLocaleString("en-CA", { minimumFractionDigits: 2 })}` : "-";
const formatDate = (iso) => {
  if (!iso) return "-";
  const [y, m, d] = iso.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(m)-1]} ${parseInt(d)}, ${y}`;
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date(today());
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const leaseStatus = (lease) => {
  if (lease.renewalStatus === "Renewed") return { label: "Renewed",         color: "#2A7BE8", bg: "#2A7BE818" };
  const days = daysUntil(lease.endDate);
  if (days === null)  return { label: "No End Date",       color: "#9993A8", bg: "#ffffff10" };
  if (days < 0)       return { label: "Expired",           color: "#FF2D2D", bg: "#FF2D2D18" };
  if (days <= 30)     return { label: `${days}d left`,     color: "#FF2D2D", bg: "#FF2D2D18" };
  if (days <= 60)     return { label: `${days}d left`,     color: "#FF7A22", bg: "#FF7A2218" };
  if (days <= 90)     return { label: `${days}d left`,     color: "#F5C842", bg: "#F5C84218" };
  return               { label: "Active",                  color: "#22AA55", bg: "#22AA5518" };
};

const EmptyState = ({ icon, title, subtitle }) => (
  <div style={{ padding: "48px 24px", textAlign: "center" }}>
    <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{title}</div>
    {subtitle && <div style={{ fontSize: 13, color: "#9993A8" }}>{subtitle}</div>}
  </div>
);

const StatusChip = ({ lease }) => {
  const s = leaseStatus(lease);
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

// -- Main Component ---------------------------------------------------------
const LeasesTab = ({ leases = [], properties = [], users = [], onAddLease, onUpdateLease }) => {
  const [showForm,    setShowForm]    = useState(false);
  const [editLease,   setEditLease]   = useState(null);
  const [filterProp,  setFilterProp]  = useState("");
  const [filterStatus,setFilterStatus]= useState("all");

  const EMPTY_FORM = {
    renterId: "", propertyId: "", unit: "",
    startDate: today(), endDate: "",
    monthlyRent: "", securityDeposit: "",
    renewalStatus: "Active", noticeToVacate: false, notes: "",
  };
  const [form, setForm] = useState(EMPTY_FORM);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const renters = users.filter((u) => u.role === "renter");

  // -- Stats ------------------------------------------------------------------
  const expiring30  = leases.filter((l) => { const d = daysUntil(l.endDate); return d !== null && d >= 0 && d <= 30; }).length;
  const expiring60  = leases.filter((l) => { const d = daysUntil(l.endDate); return d !== null && d > 30 && d <= 60; }).length;
  const expired     = leases.filter((l) => { const d = daysUntil(l.endDate); return d !== null && d < 0 && l.renewalStatus !== "Renewed"; }).length;
  const active      = leases.filter((l) => leaseStatus(l).label === "Active").length;

  // -- Filtered leases --------------------------------------------------------
  const filtered = leases.filter((l) => {
    if (filterProp && String(l.propertyId) !== String(filterProp)) return false;
    if (filterStatus === "expiring") { const d = daysUntil(l.endDate); return d !== null && d >= 0 && d <= 90; }
    if (filterStatus === "expired")  { const d = daysUntil(l.endDate); return d !== null && d < 0; }
    if (filterStatus === "active")   return leaseStatus(l).label === "Active";
    return true;
  });

  // -- Handlers --------------------------------------------------------------
  const handleSubmit = async () => {
    if (!form.renterId || !form.propertyId) return;
    const renter   = renters.find((u) => String(u.id) === String(form.renterId));
    const property = properties.find((p) => String(p.id) === String(form.propertyId));
    const data = {
      ...form,
      renterName:   renter?.name || "",
      renterEmail:  renter?.email || "",
      propertyName: property?.address || property?.name || "",
      monthlyRent:  parseFloat(form.monthlyRent) || 0,
      securityDeposit: parseFloat(form.securityDeposit) || 0,
    };
    if (editLease) {
      if (onUpdateLease) await onUpdateLease(editLease.id, data);
    } else {
      if (onAddLease) await onAddLease(data);
    }
    setForm(EMPTY_FORM);
    setShowForm(false);
    setEditLease(null);
  };

  const handleEdit = (lease) => {
    setForm({ ...lease });
    setEditLease(lease);
    setShowForm(true);
  };

  const handleMarkRenewed = async (lease) => {
    if (onUpdateLease) await onUpdateLease(lease.id, { renewalStatus: "Renewed" });
  };

  const handleNoticeVacate = async (lease) => {
    if (onUpdateLease) await onUpdateLease(lease.id, { noticeToVacate: true });
  };

  return (
    <div>
      <div style={S.pageTitle}>Leases</div>
      <div style={S.pageSub}>Track lease terms, expiry dates and renewals</div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px,1fr))", gap: 14, marginBottom: 28 }}>
        {[
          { label: "Active",          value: active,     color: "#22AA55" },
          { label: "Expiring 30 days",value: expiring30,  color: "#FF2D2D" },
          { label: "Expiring 60 days",value: expiring60,  color: "#FF7A22" },
          { label: "Expired",         value: expired,     color: "#9993A8" },
        ].map((st) => (
          <div key={st.label} style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, padding: "16px 18px" }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: st.color }}>{st.value}</div>
            <div style={{ fontSize: 11, color: "#9993A8", fontWeight: 600, marginTop: 4 }}>{st.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[["all","All"],["active","Active"],["expiring","Expiring"],["expired","Expired"]].map(([v,l]) => (
            <button key={v}
              style={{ ...S.filterBtn, ...(filterStatus === v ? S.filterBtnActive : {}) }}
              onClick={() => setFilterStatus(v)}
            >{l}</button>
          ))}
          <select style={{ ...S.select, width: "auto", fontSize: 12, padding: "5px 10px" }}
            value={filterProp} onChange={(e) => setFilterProp(e.target.value)}>
            <option value="">All Properties</option>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.address || p.name}</option>)}
          </select>
        </div>
        <button style={{ ...S.btnPrimary, fontSize: 12, padding: "8px 16px" }} onClick={() => { setEditLease(null); setForm(EMPTY_FORM); setShowForm(true); }}>
          + Add Lease
        </button>
      </div>

      {/* Add / Edit Form */}
      {showForm && (
        <div style={{ background: "#141418", border: "1px solid #E8622A44", borderRadius: 14, padding: 24, marginBottom: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 20 }}>{editLease ? "Edit Lease" : "New Lease"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div style={S.field}>
              <label style={S.label}>Property *</label>
              <select style={S.select} value={form.propertyId} onChange={(e) => { set("propertyId", e.target.value); set("renterId", ""); }}>
                <option value="">-- Select --</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.address || p.name}</option>)}
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>Tenant *</label>
              <select style={S.select} value={form.renterId} onChange={(e) => {
                set("renterId", e.target.value);
                const r = renters.find((u) => String(u.id) === e.target.value);
                if (r) { set("unit", r.unit || ""); set("propertyId", String(r.propertyId) || form.propertyId); }
              }}>
                <option value="">-- Select --</option>
                {renters.filter((u) => !form.propertyId || String(u.propertyId) === String(form.propertyId)).map((u) => (
                  <option key={u.id} value={u.id}>{u.name} - Unit {u.unit}</option>
                ))}
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>Unit</label>
              <input style={S.input} placeholder="e.g. 3" value={form.unit} onChange={(e) => set("unit", e.target.value)} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div style={S.field}>
              <label style={S.label}>Start Date</label>
              <input style={S.input} type="date" value={form.startDate} onChange={(e) => set("startDate", e.target.value)} />
            </div>
            <div style={S.field}>
              <label style={S.label}>End Date</label>
              <input style={S.input} type="date" value={form.endDate} onChange={(e) => set("endDate", e.target.value)} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Monthly Rent ($)</label>
              <input style={S.input} type="number" placeholder="2400" value={form.monthlyRent} onChange={(e) => set("monthlyRent", e.target.value)} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Security Deposit ($)</label>
              <input style={S.input} type="number" placeholder="2400" value={form.securityDeposit} onChange={(e) => set("securityDeposit", e.target.value)} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div style={S.field}>
              <label style={S.label}>Renewal Status</label>
              <select style={S.select} value={form.renewalStatus} onChange={(e) => set("renewalStatus", e.target.value)}>
                {["Active","Renewed","Not Renewing","Month-to-Month","Pending Renewal"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>Notice to Vacate Sent</label>
              <select style={S.select} value={form.noticeToVacate ? "yes" : "no"} onChange={(e) => set("noticeToVacate", e.target.value === "yes")}>
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>Notes</label>
              <input style={S.input} placeholder="Optional notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={{ ...S.btnPrimary, padding: "10px 20px", fontSize: 13 }} onClick={handleSubmit}>
              {editLease ? "Save Changes" : "Add Lease"}
            </button>
            <button style={{ ...S.btnSecondary, padding: "10px 20px", fontSize: 13 }} onClick={() => { setShowForm(false); setEditLease(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Lease list */}
      <div style={S.tableWrap}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 130px 100px 110px 110px 90px 160px", gap: 10, padding: "10px 20px", background: "#0F0F14", borderBottom: "1px solid #ffffff08" }}>
          {["Tenant / Property", "Lease Period", "Rent/mo", "Deposit", "Renewal", "Notice", "Actions"].map((h) => (
            <div key={h} style={{ fontSize: 11, fontWeight: 700, color: "#5A5570", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
          ))}
        </div>

        {filtered.map((lease) => {
          const status = leaseStatus(lease);
          const days   = daysUntil(lease.endDate);
          return (
            <div key={lease.id} style={{ display: "grid", gridTemplateColumns: "1fr 130px 100px 110px 110px 90px 160px", gap: 10, padding: "14px 20px", borderBottom: "1px solid #ffffff06", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{lease.renterName || "-"}</div>
                <div style={{ fontSize: 11, color: "#9993A8", marginTop: 2 }}>{lease.propertyName} . Unit {lease.unit}</div>
                {lease.notes && <div style={{ fontSize: 10, color: "#5A5570", marginTop: 2 }}>{lease.notes}</div>}
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#9993A8" }}>{formatDate(lease.startDate)}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: status.color }}>{formatDate(lease.endDate)}</span>
                </div>
                <StatusChip lease={lease} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#F0EDE8" }}>{fmtC(lease.monthlyRent)}</div>
              <div style={{ fontSize: 12, color: "#9993A8" }}>{fmtC(lease.securityDeposit)}</div>
              <div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 99,
                  background: lease.renewalStatus === "Active" ? "#22AA5518" :
                              lease.renewalStatus === "Renewed" ? "#2A7BE818" :
                              lease.renewalStatus === "Not Renewing" ? "#FF2D2D18" : "#ffffff10",
                  color: lease.renewalStatus === "Active" ? "#22AA55" :
                         lease.renewalStatus === "Renewed" ? "#2A7BE8" :
                         lease.renewalStatus === "Not Renewing" ? "#FF2D2D" : "#9993A8",
                }}>
                  {lease.renewalStatus}
                </span>
              </div>
              <div>
                {lease.noticeToVacate
                  ? <span style={{ fontSize: 11, fontWeight: 700, color: "#FF7A22" }}>Sent</span>
                  : <span style={{ fontSize: 11, color: "#5A5570" }}>Not sent</span>
                }
              </div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                <button style={{ ...S.btnSmall, background: "#ffffff10", color: "#9993A8", fontSize: 10, padding: "4px 8px" }}
                  onClick={() => handleEdit(lease)}>Edit</button>
                {lease.renewalStatus !== "Renewed" && days !== null && days <= 90 && (
                  <button style={{ ...S.btnSmall, background: "#2A7BE818", color: "#2A7BE8", fontSize: 10, padding: "4px 8px" }}
                    onClick={() => handleMarkRenewed(lease)}>Renewed</button>
                )}
                {!lease.noticeToVacate && (
                  <button style={{ ...S.btnSmall, background: "#FF7A2218", color: "#FF7A22", fontSize: 10, padding: "4px 8px" }}
                    onClick={() => handleNoticeVacate(lease)}>Notice</button>
                )}
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <EmptyState icon="📄" title="No leases found" subtitle={leases.length ? "Try changing the filter" : "Add your first lease to start tracking expiry dates"} />
        )}
      </div>

      {/* Expiry timeline - only show if leases exist */}
      {leases.length > 0 && (
        <div style={{ marginTop: 24, background: "#141418", border: "1px solid #ffffff08", borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>Expiry Timeline - Next 6 Months</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {leases
              .filter((l) => { const d = daysUntil(l.endDate); return d !== null && d >= 0 && d <= 180; })
              .sort((a, b) => new Date(a.endDate) - new Date(b.endDate))
              .map((l) => {
                const d = daysUntil(l.endDate);
                const pct = Math.min(100, Math.round((d / 180) * 100));
                const color = d <= 30 ? "#FF2D2D" : d <= 60 ? "#FF7A22" : "#22AA55";
                return (
                  <div key={l.id}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: "#F0EDE8", fontWeight: 600 }}>{l.renterName} - Unit {l.unit}</span>
                      <span style={{ color }}>{d} days . {formatDate(l.endDate)}</span>
                    </div>
                    <div style={{ background: "#ffffff10", borderRadius: 99, height: 6, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 99, transition: "width 0.3s" }} />
                    </div>
                  </div>
                );
              })
            }
            {leases.filter((l) => { const d = daysUntil(l.endDate); return d !== null && d >= 0 && d <= 180; }).length === 0 && (
              <div style={{ fontSize: 13, color: "#5A5570" }}>No leases expiring in the next 6 months</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeasesTab;
